import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatCurrency } from "../lib/api";
import { Plus, Edit, Trash2, X, UserCheck, UserX, Mail, Phone } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import ResponsiveTable from "../components/ResponsiveTable";
import PasswordInput from "../components/PasswordInput";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  status: "Active",
  commission_rate: 10,
  monthly_target: 0,
  join_date: "",
  notes: "",
  login_password: "",
};

export default function SalesUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/sales-users");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...EMPTY, ...s,
      join_date: (s.join_date || "").slice(0, 10),
      login_password: "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        commission_rate: parseFloat(form.commission_rate),
        monthly_target: parseFloat(form.monthly_target) || 0,
        join_date: form.join_date ? new Date(form.join_date).toISOString() : null,
      };
      if (!payload.login_password) delete payload.login_password;
      if (editing) {
        delete payload.login_password;
        await api.patch(`/sales-users/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/sales-users", payload);
        toast.success("Sales user added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const toggleStatus = async (s) => {
    const next = s.status === "Active" ? "Inactive" : "Active";
    try {
      await api.patch(`/sales-users/${s.id}`, { status: next });
      toast.success(`${s.name} → ${next}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete ${s.name}? (Their past commissions will remain.)`)) return;
    try {
      await api.delete(`/sales-users/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout
      title="Sales Users"
      subtitle="Commission roster"
      action={
        <button onClick={openCreate} data-testid="add-sales-user" className="btn-gold px-5 py-3 inline-flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Add Sales Person
        </button>
      }
    >
      <Toaster theme="dark" position="top-right" />

      <SalesUsersTable
        items={items}
        onToggleStatus={toggleStatus}
        onEdit={openEdit}
        onDelete={onDelete}
      />

      {open && (
        <SalesUserModal
          form={form}
          setForm={setForm}
          editing={editing}
          onClose={() => setOpen(false)}
          onSubmit={submit}
        />
      )}
    </Layout>
  );
}

function SalesUsersTable({ items, onToggleStatus, onEdit, onDelete }) {
  const columns = [
    { key: "name", label: "Name", cell: (s) => (
      <div className="min-w-0">
        <div className="truncate">{s.name}</div>
        {s.notes && <div className="text-[0.7rem] text-[#666] mt-0.5 italic line-clamp-1">{s.notes}</div>}
      </div>
    ) },
    { key: "contact", label: "Contact", cell: (s) => (
      <div className="text-xs text-[#8A8A8A]">
        {s.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3 h-3 shrink-0" /> {s.email}</div>}
        {s.phone && <div className="flex items-center gap-2 mono mt-1"><Phone className="w-3 h-3 shrink-0" /> {s.phone}</div>}
      </div>
    ), hideOnMobile: true },
    { key: "rate", label: "Commission", align: "right", width: "120px", cell: (s) => <span className="mono text-[#D4AF37]">{s.commission_rate?.toFixed(2)}%</span> },
    { key: "target", label: "Target", align: "right", width: "140px", cell: (s) => <span className="mono text-[#7BA585]" data-testid={`target-${s.id}`}>{s.monthly_target ? formatCurrency(s.monthly_target) : "—"}</span> },
    { key: "joined", label: "Joined", width: "110px", cell: (s) => <span className="mono text-xs text-[#8A8A8A]">{(s.join_date || "").slice(0, 10)}</span>, hideOnMobile: true },
    { key: "status", label: "Status", width: "110px", cell: (s) => (
      <span className={`status-badge ${s.status === "Active" ? "status-available" : "status-sold"}`}>{s.status}</span>
    ) },
  ];
  const rowActions = (s) => (
    <>
      <button
        data-testid={`toggle-${s.id}`}
        onClick={() => onToggleStatus(s)}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37] inline-flex items-center justify-center"
        title={s.status === "Active" ? "Deactivate" : "Activate"}
        aria-label="Toggle status"
      >
        {s.status === "Active" ? <UserX className="w-4 h-4" strokeWidth={1.25} /> : <UserCheck className="w-4 h-4" strokeWidth={1.25} />}
      </button>
      <button data-testid={`edit-${s.id}`} onClick={() => onEdit(s)} className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37] inline-flex items-center justify-center" aria-label="Edit">
        <Edit className="w-4 h-4" strokeWidth={1.25} />
      </button>
      <button data-testid={`delete-${s.id}`} onClick={() => onDelete(s)} className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 inline-flex items-center justify-center" aria-label="Delete">
        <Trash2 className="w-4 h-4" strokeWidth={1.25} />
      </button>
    </>
  );
  return (
    <ResponsiveTable
      columns={columns}
      rows={items}
      rowKey={(s) => s.id}
      testid="sales-users-table"
      rowActions={rowActions}
      emptyMessage="No sales users yet — add your first to enable per-sale commission tracking."
      mobileCard={(s) => ({
        title: <span>{s.name}</span>,
        subtitle: <span className="text-[#8A8A8A]">{s.email || s.phone || "—"}</span>,
        fields: [
          { label: "Commission", value: <span className="mono text-[#D4AF37]">{s.commission_rate?.toFixed(2)}%</span> },
          { label: "Target", value: <span className="mono text-[#7BA585]">{s.monthly_target ? formatCurrency(s.monthly_target) : "—"}</span> },
          { label: "Status", value: <span className={`status-badge ${s.status === "Active" ? "status-available" : "status-sold"}`}>{s.status}</span> },
          { label: "Joined", value: <span className="mono">{(s.join_date || "").slice(0, 10) || "—"}</span> },
          { label: "Phone", value: <span className="mono">{s.phone || "—"}</span> },
          ...(s.notes ? [{ label: "Notes", value: <span className="italic text-[#8A8A8A]">{s.notes}</span> }] : []),
        ],
        actions: rowActions(s),
      })}
    />
  );
}

function SalesUserModal({ form, setForm, editing, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-xl fade-up" data-testid="sales-user-modal">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">{editing ? "Edit Sales User" : "New Sales User"}</div>
            <h3 className="serif text-2xl mt-1">{editing ? editing.name : "Add to Roster"}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <form onSubmit={onSubmit} className="px-7 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="su-name" />
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm">
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
          </Field>
          <Field label="Commission Rate (%)">
            <input required type="number" step="0.1" min="0" max="100" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="su-rate" />
          </Field>
          <Field label="Monthly Target (IDR)">
            <input type="number" step="1" min="0" value={form.monthly_target} onChange={(e) => setForm({ ...form, monthly_target: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" placeholder="e.g. 100000000" data-testid="su-target" />
          </Field>
          <Field label="Join Date">
            <input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm min-h-[60px]" />
            </Field>
          </div>
          {!editing && (
            <div className="md:col-span-2">
              <Field label="Login Password (optional — enables 'Sales User' role login)">
                <PasswordInput value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" placeholder="Leave blank to create roster entry only" data-testid="su-password" />
              </Field>
              <div className="text-[0.65rem] text-[#666] mt-1">
                If set with an email, an auth account is created so this person can log in and see their own performance.
              </div>
            </div>
          )}
          <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button type="submit" data-testid="submit-sales-user" className="btn-gold px-5 py-2.5">{editing ? "Save Changes" : "Add Sales User"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}
